import React, { useCallback, useEffect, useState } from 'react'

import { useNavigate } from 'react-router-dom';
import { 
    Box, 
    CircularProgress, 
    Card, 
    CardContent, 
    Typography, 
    Chip,
    Paper,
    Button,
    Stack
} from '@mui/material';
import { 
    Person as PersonIcon,
    Email as EmailIcon,
    AdminPanelSettings as AdminIcon,
    Code as DevIcon,
    AccountCircle as UserIcon,
    Clear as ClearIcon
} from '@mui/icons-material';

import { useAppContext } from 'app/AppContext';
import DataTable from 'components/DataTable';
import DataFilter from 'components/DataFilter';
import DataFilterTextField from 'components/DataFilter/DataFilterTextField';
import DataFilterSelect from 'components/DataFilter/DataFilterSelect';
import { fetchArray } from 'entity';
import Perfil from 'entity/Perfil';

export default function PerfilConsulta() {
    const navigate = useNavigate();
    const [options, setOptions] = useState([]);
    const [filteredOptions, setFilteredOptions] = useState([]);
    const [filters, setFilters] = useState({});
    const { useSetTitle, setActionsView } = useAppContext()
    const [loading, setLoading] = useState(true)

    useSetTitle("Consulta de Perfis");

    // Função para obter ícone baseado no tipo de perfil
    const getTipoPerfilIcon = (tipo) => {
        switch (tipo) {
            case 'admin':
                return <AdminIcon color="error" />;
            case 'empresa':
                return <DevIcon color="success" />;
            case 'colaborador':
                return <UserIcon color="primary" />;
            default:
                return <UserIcon color="primary" />;
        }
    };

    // Função para obter chip colorido baseado no tipo
    const getTipoPerfilChip = (tipo) => {
        const labels = {
            'admin': 'ADMINISTRADOR',
            'empresa': 'EMPRESA',
            'colaborador': 'COLABORADOR'
        };
        
        const colors = {
            'admin': 'error',
            'empresa': 'success',
            'colaborador': 'primary'
        };

        return (
            <Chip
                icon={getTipoPerfilIcon(tipo)}
                label={labels[tipo] || 'COLABORADOR'}
                color={colors[tipo] || 'primary'}
                size="small"
                variant="outlined"
            />
        );
    };

    // Colunas customizadas com melhor visual
    const customColumns = [
        { 
            field: "name", 
            headerName: "Nome", 
            flex: 2,
            renderCell: (params) => (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <PersonIcon color="primary" fontSize="small" />
                    <Typography variant="body2" fontWeight={500}>
                        {params.value}
                    </Typography>
                </Box>
            )
        },
        { 
            field: "email", 
            headerName: "E-Mail", 
            flex: 2,
            renderCell: (params) => (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <EmailIcon color="action" fontSize="small" />
                    <Typography variant="body2" color="text.secondary">
                        {params.value}
                    </Typography>
                </Box>
            )
        },
        {
            field: "tipoPerfil",
            headerName: "Tipo de Perfil",
            flex: 1.5,
            align: 'center',
            headerAlign: 'center',
            renderCell: (params) => getTipoPerfilChip(params.value)
        },
    ];

    useEffect(() => {
        const actions = [
            {
                uid: "novoForm",
                text: "Novo Usuário",
                iconName: "Add",
                sequence: "10",
                action: () => {
                    navigate("/createUser");
                },
            },
        ];

        setActionsView(actions);

        return () => {
            setActionsView([]);
        };
    }, [navigate, setActionsView]);

    const loadOptions = useCallback(async () => {
        if (options.length === 0) {
            try {
                const result = await fetchArray(Perfil.getAll());
                const processedResult = result.map(item => ({
                    ...item,
                    tipoPerfil: item.tipoPerfil || 'colaborador'
                }));
                setOptions(processedResult);
                setFilteredOptions(processedResult);
                setLoading(false);
            } catch (error) {
                console.error('Erro ao carregar perfis:', error);
                setLoading(false);
            }
        }
    }, [options.length]);

    // Aplicar filtros
    useEffect(() => {
        let filtered = [...options];

        if (filters.name?.value) {
            filtered = filtered.filter(item => 
                item.name?.toLowerCase().includes(filters.name.value.toLowerCase())
            );
        }

        if (filters.email?.value) {
            filtered = filtered.filter(item => 
                item.email?.toLowerCase().includes(filters.email.value.toLowerCase())
            );
        }

        if (filters.tipoPerfil?.value) {
            const tipoValue = filters.tipoPerfil.value.value || filters.tipoPerfil.value;
            filtered = filtered.filter(item => 
                item.tipoPerfil === tipoValue
            );
        }

        setFilteredOptions(filtered);
    }, [filters, options]);

    useEffect(() => {
        loadOptions();
    }, [loadOptions]);

    const tipoPerfilOptions = [
        { value: 'admin', label: 'ADMINISTRADOR' },
        { value: 'empresa', label: 'EMPRESA' },
        { value: 'colaborador', label: 'COLABORADOR' }
    ];

    const clearFilters = () => {
        setFilters({});
    };

    return (
        <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
            {/* Área de Filtros */}
            <Paper elevation={1} sx={{ mb: 2 }}>
                <Stack direction="row" spacing={2} alignItems="center" sx={{ p: 1 }}>
                    <Box sx={{ flex: 1 }}>
                        <DataFilter filters={filters} setFilters={setFilters}>
                            <DataFilterTextField
                                name="name"
                                label="Nome do usuário"
                                autocompleteOptions={[...new Set(options.map(item => item.name).filter(Boolean))]}
                            />
                            <DataFilterTextField
                                name="email"
                                label="E-mail"
                                autocompleteOptions={[...new Set(options.map(item => item.email).filter(Boolean))]}
                            />
                            <DataFilterSelect
                                name="tipoPerfil"
                                label="Tipo de Perfil"
                                options={tipoPerfilOptions}
                            />
                        </DataFilter>
                    </Box>
                    <Button
                        variant="outlined"
                        size="small"
                        startIcon={<ClearIcon />}
                        onClick={clearFilters}
                        sx={{ minWidth: 120 }}
                    >
                        Limpar Filtros
                    </Button>
                </Stack>
            </Paper>

            {/* Estatísticas */}
            <Box sx={{ display: 'flex', gap: 2, mb: 2, flexWrap: 'wrap' }}>
                <Card sx={{ minWidth: 120 }}>
                    <CardContent sx={{ p: 2, '&:last-child': { pb: 2 } }}>
                        <Typography variant="h6" color="primary">
                            {filteredOptions.length}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                            Total de Perfis
                        </Typography>
                    </CardContent>
                </Card>
                
                <Card sx={{ minWidth: 120 }}>
                    <CardContent sx={{ p: 2, '&:last-child': { pb: 2 } }}>
                        <Typography variant="h6" color="error">
                            {filteredOptions.filter(p => p.tipoPerfil === 'admin').length}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                            Administradores
                        </Typography>
                    </CardContent>
                </Card>

                <Card sx={{ minWidth: 120 }}>
                    <CardContent sx={{ p: 2, '&:last-child': { pb: 2 } }}>
                        <Typography variant="h6" color="success.main">
                            {filteredOptions.filter(p => p.tipoPerfil === 'empresa').length}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                            Empresas
                        </Typography>
                    </CardContent>
                </Card>

                <Card sx={{ minWidth: 120 }}>
                    <CardContent sx={{ p: 2, '&:last-child': { pb: 2 } }}>
                        <Typography variant="h6" color="primary">
                            {filteredOptions.filter(p => p.tipoPerfil === 'colaborador' || (!p.tipoPerfil)).length}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                            Colaboradores
                        </Typography>
                    </CardContent>
                </Card>
            </Box>

            {/* Tabela de Dados */}
            <Box sx={{ flex: 1, minHeight: 400 }}>
                <DataTable
                    columns={customColumns}
                    rows={filteredOptions}
                    onRowClick={(row) => {
                        navigate("/perfil?uid=" + row.id);
                    }}
                    components={{
                        LoadingOverlay: CircularProgress,
                    }}
                    loading={loading}
                    sx={{
                        "& .MuiDataGrid-row:hover": {
                            cursor: "pointer",
                            backgroundColor: "rgba(25, 118, 210, 0.08)",
                        },
                        '& .MuiDataGrid-row:nth-of-type(odd)': {
                            backgroundColor: "rgba(0, 0, 0, 0.02)",
                        },
                        '& .MuiDataGrid-columnHeaders': {
                            backgroundColor: 'rgba(25, 118, 210, 0.1)',
                            fontWeight: 600,
                        },
                        '& .MuiDataGrid-cell': {
                            borderColor: 'rgba(224, 224, 224, 1)',
                        },
                        '& .MuiDataGrid-root': {
                            border: 'none',
                        }
                    }}
                    initialState={{
                        pagination: {
                            paginationModel: { pageSize: 25 },
                        },
                    }}
                    pageSizeOptions={[10, 25, 50, 100]}
                    disableRowSelectionOnClick
                />
            </Box>
        </Box>
    )
}